import { apiRequest } from "./apiClient";
import type { PlanTier } from "./billingApi";
import type { UserCvSource } from "./userCvApi";

export type AdminUser = {
  id: string;
  email: string;
  name: string;
  role: "user" | "admin";
  provider: "google" | "local";
  profileImageUrl: string;
  loginCount: number;
  lastLoginAt: string | null;
  createdAt: string | null;
  cvCount: number;
  downloadCredits: number;
  purchasedCredits: number;
  subscriptionCredits: number;
  bonusCredits: number;
  premiumDesignDownloads: number;
  totalPremiumDesignDownloadsGranted: number;
  hasUnlimitedDownloads: boolean;
  totalDownloadsUsed: number;
  totalCreditsPurchased: number;
  totalBonusCreditsGranted: number;
  lastDownloadAt: string | null;
  planTier: PlanTier;
  jobRole: string;
  fieldOfWork: string;
  isEmailVerified: boolean;
};

export type AdminTemplateStat = {
  templateKey: string;
  label: string;
  accentColor: string;
  isActive: boolean;
  tier: "CLASSIC_LEGACY" | "PREMIUM";
  count: number;
  last30Days: number;
  /** 0–1 share of all CVs. */
  share: number;
};

export type AdminStats = {
  totals: {
    users: number;
    admins: number;
    cvs: number;
    cvsLast30Days: number;
    logins: number;
    googleUsers: number;
    localUsers: number;
    coverLetters: number;
    uploads: number;
  };
  plans: Record<PlanTier, number>;
  recentUsers: Array<{
    id: string;
    email: string;
    name: string;
    role: "user" | "admin";
    provider: "google" | "local";
    profileImageUrl: string;
    loginCount: number;
    lastLoginAt: string | null;
    createdAt: string | null;
  }>;
  seo: {
    topTitles: Array<{ title: string; count: number }>;
    topTemplates: Array<{ templateKey: string; count: number }>;
  };
  designs: {
    templates: AdminTemplateStat[];
    premiumCount: number;
    legacyCount: number;
    colors: Array<{ color: string; count: number; share: number }>;
  };
  analysis: {
    total: number;
    last30Days: number;
    uniqueUsers: number;
    averageScore: number;
    bestScore: number;
    distribution: Array<{ label: string; count: number }>;
    byTemplate: Array<{
      templateKey: string;
      count: number;
      averageScore: number;
    }>;
    topTargetRoles: Array<{ role: string; count: number }>;
  };
  uploads: {
    total: number;
    bySource: Record<UserCvSource, number>;
    last30Days: Record<UserCvSource, number>;
  };
};

// ─── Dashboard drill-downs ─────────────────────────────────────────────────
// The stats above answer "how many"; these answer "who".

export type AdminPage<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
};

/** The account a drill-down row belongs to. */
export type AdminRowUser = {
  id: string;
  name: string;
  email: string;
  profileImageUrl: string;
  planTier: PlanTier;
};

export type DesignUsageRow = {
  id: string;
  user: AdminRowUser;
  cvName: string;
  cvTitle: string;
  templateKey: string;
  templateLabel: string;
  tier: "CLASSIC_LEGACY" | "PREMIUM";
  accentColor: string;
  isPublished: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type AnalysisRunRow = {
  id: string;
  user: AdminRowUser;
  cvId: string;
  cvTitle: string;
  templateKey: string;
  templateLabel: string;
  accentColor: string;
  atsScore: number;
  targetRole: string;
  sectionScores: {
    summary: number;
    experience: number;
    skills: number;
    education: number;
    overall: number;
  };
  suggestionCount: number;
  createdAt: string | null;
};

export type UploadRow = {
  id: string;
  user: AdminRowUser;
  source: UserCvSource;
  originalFileName: string;
  mimeType: string;
  fileSize: number;
  s3FileUrl: string;
  parsedName: string;
  parsedHeadline: string;
  createdAt: string | null;
};

/** ASCII keys matching the score bands the chart shows. */
export const ANALYSIS_BANDS = ["0-40", "41-60", "61-80", "81-100"] as const;
export type AnalysisBand = (typeof ANALYSIS_BANDS)[number];

export type DesignUsageQuery = {
  page?: number;
  limit?: number;
  search?: string;
  templateKey?: string;
  accentColor?: string;
};

export type AnalysisRunQuery = {
  page?: number;
  limit?: number;
  search?: string;
  band?: AnalysisBand;
  templateKey?: string;
};

export type UploadRowQuery = {
  page?: number;
  limit?: number;
  search?: string;
  source?: UserCvSource;
};

const insightQuery = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const listDesignUsage = (query: DesignUsageQuery = {}) =>
  apiRequest<AdminPage<DesignUsageRow>>(
    `/admin/insights/designs${insightQuery(query)}`,
  );

export const listAnalysisRuns = (query: AnalysisRunQuery = {}) =>
  apiRequest<AdminPage<AnalysisRunRow>>(
    `/admin/insights/analysis${insightQuery(query)}`,
  );

export const listUploadRows = (query: UploadRowQuery = {}) =>
  apiRequest<AdminPage<UploadRow>>(
    `/admin/insights/uploads${insightQuery(query)}`,
  );

export type SiteNoticeVariant = "info" | "success" | "warning" | "danger";

export type SiteNotice = {
  enabled: boolean;
  message: string;
  variant: SiteNoticeVariant;
  linkLabel: string;
  linkUrl: string;
  startsAt: string | null;
  endsAt: string | null;
  updatedBy: string;
  updatedAt: string | null;
};

export type PublicSiteNotice = SiteNotice & {
  active: boolean;
};

export type UpsertSiteNoticePayload = {
  enabled: boolean;
  message: string;
  variant: SiteNoticeVariant;
  linkLabel?: string;
  linkUrl?: string;
  startsAt?: string;
  endsAt?: string;
};

export const getAdminStats = () => apiRequest<AdminStats>("/admin/stats");

export const listAdminUsers = () => apiRequest<AdminUser[]>("/admin/users");

export const getPublicSiteNotice = () =>
  apiRequest<PublicSiteNotice>("/admin/site-notice", { skipRefresh: true });

export const getAdminSiteNotice = () =>
  apiRequest<SiteNotice>("/admin/site-notice/manage");

export const upsertSiteNotice = (payload: UpsertSiteNoticePayload) =>
  apiRequest<SiteNotice>("/admin/site-notice", {
    method: "POST",
    body: JSON.stringify(payload),
  });

// ─── AI Logs ────────────────────────────────────────────────────────────────

export type InlineLogEntry = {
  _id: string;
  fieldType: string;
  inputText: string;
  outputText: string;
  tone?: string;
  length?: string;
  format?: string;
  model?: string;
  latencyMs?: number;
  wasRepaired: boolean;
  userId?: string;
  createdAt: string;
};

export type TraceLogEntry = {
  _id: string;
  traceName: string;
  traceType: string;
  provider?: string;
  model?: string;
  output?: unknown;
  usage?: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  latencyMs?: number;
  success: boolean;
  errorMessage?: string;
  userId?: string;
  createdAt: string;
};

export type PaginatedResult<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
};

export type DailyCount = { date: string; count: number };

export type AiLogsStats = {
  inline: {
    total: number;
    repairedCount: number;
    avgLatencyMs: number | null;
    byFieldType: Array<{ _id: string; count: number }>;
    byModel: Array<{ _id: string; count: number }>;
    daily30d: DailyCount[];
  };
  traces: {
    total: number;
    successCount: number;
    avgLatencyMs: number | null;
    totalPromptTokens: number;
    totalCompletionTokens: number;
    byTraceType: Array<{ _id: string; count: number }>;
    byProvider: Array<{ _id: string; count: number }>;
    daily30d: DailyCount[];
  };
};

export type InlineLogsQuery = {
  from?: string;
  to?: string;
  fieldType?: string;
  model?: string;
  wasRepaired?: boolean;
  userId?: string;
  page?: number;
  limit?: number;
};

export type TraceLogsQuery = {
  from?: string;
  to?: string;
  traceType?: string;
  provider?: string;
  model?: string;
  success?: boolean;
  userId?: string;
  page?: number;
  limit?: number;
};

const toQueryString = (params: Record<string, unknown>): string => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "" && v !== null)
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join("&");
  return qs ? `?${qs}` : "";
};

export const getAiLogsStats = () =>
  apiRequest<AiLogsStats>("/admin/ai-logs/stats");

export const queryInlineLogs = (q: InlineLogsQuery = {}) =>
  apiRequest<PaginatedResult<InlineLogEntry>>(
    `/admin/ai-logs/inline${toQueryString(q as Record<string, unknown>)}`,
  );

export const queryTraceLogs = (q: TraceLogsQuery = {}) =>
  apiRequest<PaginatedResult<TraceLogEntry>>(
    `/admin/ai-logs/traces${toQueryString(q as Record<string, unknown>)}`,
  );

// ─── Template Management ───────────────────────────────────────────────────

export type AdminTemplate = {
  _id: string;
  key: string;
  label: string;
  description: string;
  accentColor: string;
  family: string;
  isActive: boolean;
  isPaid: boolean;
};

export const listAdminTemplates = () =>
  apiRequest<AdminTemplate[]>("/admin/templates");

export const toggleTemplates = (keys: string[], isActive: boolean) =>
  apiRequest<AdminTemplate[]>("/admin/templates/toggle", {
    method: "PATCH",
    body: JSON.stringify({ keys, isActive }),
  });

export const toggleTemplatesPaidStatus = (keys: string[], isPaid: boolean) =>
  apiRequest<AdminTemplate[]>("/admin/templates/toggle-paid", {
    method: "PATCH",
    body: JSON.stringify({ keys, isPaid }),
  });

// ─── Dev Environment Allowlist ─────────────────────────────────────────────

export type DevAllowlistEntry = {
  _id: string;
  email: string;
  addedBy: string;
  note?: string;
  createdAt: string | null;
  source?: 'env' | 'database' | 'both';
  isEnvConfigured?: boolean;
};

export type DevAllowlistResponse = {
  entries: DevAllowlistEntry[];
  envEmails: string[];
  allowedDomains: string[];
  isRestricted: boolean;
};

export const getDevAllowlist = () =>
  apiRequest<DevAllowlistResponse | DevAllowlistEntry[]>("/admin/dev-allowlist");

export const addDevAllowedEmails = (emails: string[], note?: string) =>
  apiRequest<{ added: string[]; existing: string[]; invalid: string[] }>(
    "/admin/dev-allowlist",
    {
      method: "POST",
      body: JSON.stringify({ emails, note }),
    },
  );

export const removeDevAllowedEmail = (email: string) =>
  apiRequest<{ removed: boolean; isEnvConfigured?: boolean }>(
    `/admin/dev-allowlist/${encodeURIComponent(email)}`,
    {
      method: "DELETE",
    },
  );

// ─── Admin Management ──────────────────────────────────────────────────────

export type AdminAccount = {
  id: string;
  email: string;
  name: string;
  role: string;
  provider: string;
  profileImageUrl: string;
  isEmailVerified: boolean;
  isTwoFactorEnabled: boolean;
  twoFactorMethod: string;
  lastLoginAt: string | null;
  createdAt: string | null;
};

export const listAdmins = () =>
  apiRequest<AdminAccount[]>("/admin/admins");

export const createOrPromoteAdmin = (payload: {
  email: string;
  name?: string;
  password?: string;
}) =>
  apiRequest<{ user: AdminAccount; isNew: boolean; message: string }>(
    "/admin/admins",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

export const revokeAdmin = (id: string) =>
  apiRequest<{ message: string }>(`/admin/admins/${id}`, {
    method: "DELETE",
  });


// ─── Email delivery ──────────────────────────────────────────────────────────

/** The services that can send the app's email. */
export type EmailProvider = "resend" | "ses";

/** Which service sends the app's email, and how each one stands. */
export type EmailDeliveryStatus = {
  provider: EmailProvider;
  updatedBy?: string;
  updatedAt?: string;
  from: string;
  resend: { configured: boolean };
  ses: {
    configured: boolean;
    region: string;
    /** Its own key pair, or the server's AWS role. */
    usesOwnKeys: boolean;
    account?: {
      sendingEnabled: boolean;
      /** False = sandbox: SES only sends to verified addresses. */
      productionAccess: boolean;
      max24HourSend?: number;
      maxSendRate?: number;
      sentLast24Hours?: number;
    };
    /** Why the account details aren't shown (e.g. a send-only key). */
    accountNote?: string;
  };
};

export const getEmailSettings = () =>
  apiRequest<EmailDeliveryStatus>("/admin/email/settings");

export const setEmailProvider = (provider: EmailProvider) =>
  apiRequest<EmailDeliveryStatus>("/admin/email/settings", {
    method: "POST",
    body: JSON.stringify({ provider }),
  });

/** A test email through one service, to the signed-in admin. */
export const sendTestEmail = (provider: EmailProvider) =>
  apiRequest<{ ok: boolean; message: string }>("/admin/email/test", {
    method: "POST",
    body: JSON.stringify({ provider }),
  });
